import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { trackEvent } from "@/lib/analytics";

/**
 * Reusable newsletter form. Variants:
 *  - "stacked" (default) — full form with name + email
 *  - "inline" — single email input + button
 *  - "community" — name + email + city, posts to /submissions/community
 */
export default function NewsletterForm({
  variant = "stacked",
  tone = "light",
  testidPrefix = "nl",
  endpoint = "/submissions/newsletter",
}) {
  const isCommunity = variant === "community";
  const isInline = variant === "inline";

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onDark = tone === "dark";
  const inputBase = onDark
    ? "bg-transparent border-b border-cream/40 text-cream placeholder:text-cream/40 focus:border-gold"
    : "bg-transparent border-b border-charcoal/30 text-charcoal placeholder:text-charcoal/40 focus:border-terracotta";

  const labelCls = onDark
    ? "text-cream/60"
    : "text-charcoal/60";

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    if (isCommunity && !name.trim()) return;
    setLoading(true);
    try {
      const payload = isCommunity
        ? { email, name: name.trim(), city: city.trim() || undefined, website, attribution: getAttribution() }
        : { email, name: name.trim() || undefined, website, attribution: getAttribution() };
      const { data } = await api.post(endpoint, payload);
      trackEvent("form_submit", { form_type: isCommunity ? "community" : "newsletter" });
      toast.success(data.message || "¡Gracias!");
      setDone(true);
      setEmail("");
      setName("");
      setCity("");
    } catch (err) {
      const msg =
        err.response?.status === 429
          ? "Demasiadas solicitudes. Intenta en un minuto."
          : formatApiError(err.response?.data?.detail) || "Algo salió mal.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testidPrefix}-success`}
        className={`p-6 md:p-8 border ${
          onDark
            ? "border-gold/40 bg-cream/5 text-cream"
            : "border-gold/60 bg-gold/10 text-charcoal"
        }`}
      >
        <p className="font-serif text-xl md:text-2xl mb-1">Gracias.</p>
        <p className="opacity-75 text-sm md:text-base">
          Revisa tu correo. Te escribimos pronto.
        </p>
        <button
          type="button"
          onClick={() => setDone(false)}
          className={`mt-4 text-xs underline underline-offset-4 ${
            onDark ? "text-gold" : "text-terracotta"
          }`}
        >
          Enviar otro
        </button>
      </div>
    );
  }

  // Honeypot field (shared)
  const Honeypot = (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        left: "-10000px",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <label htmlFor={`${testidPrefix}-hp`}>Sitio web (no llenar)</label>
      <input
        id={`${testidPrefix}-hp`}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
      />
    </div>
  );

  if (isInline) {
    return (
      <form
        onSubmit={submit}
        data-testid={`${testidPrefix}-form`}
        className="flex flex-col sm:flex-row gap-3 w-full"
      >
        <input
          type="email"
          required
          placeholder="tu@correo.com"
          value={email}
          data-testid={`${testidPrefix}-email`}
          onChange={(e) => setEmail(e.target.value)}
          className={`flex-1 ${inputBase} px-0 py-3 text-base focus:outline-none transition-colors`}
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testidPrefix}-submit`}
          className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60"
        >
          {loading ? "Enviando…" : "Suscríbete"}
          <ArrowRight className="h-4 w-4" />
        </button>
        {Honeypot}
      </form>
    );
  }

  // stacked / community
  return (
    <form
      onSubmit={submit}
      data-testid={`${testidPrefix}-form`}
      className="space-y-5 w-full"
    >
      {(isCommunity || variant === "stacked") && (
        <div>
          <label className={`text-[11px] uppercase tracking-[0.22em] block mb-2 ${labelCls}`}>
            Nombre {isCommunity ? "" : "(opcional)"}
          </label>
          <input
            type="text"
            required={isCommunity}
            data-testid={`${testidPrefix}-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`w-full ${inputBase} py-3 text-base focus:outline-none transition-colors`}
            placeholder="Ej. María"
          />
        </div>
      )}

      {isCommunity && (
        <div>
          <label className={`text-[11px] uppercase tracking-[0.22em] block mb-2 ${labelCls}`}>
            Ciudad
          </label>
          <input
            type="text"
            data-testid={`${testidPrefix}-city`}
            value={city}
            onChange={(e) => setCity(e.target.value)}
            className={`w-full ${inputBase} py-3 text-base focus:outline-none transition-colors`}
            placeholder="Ciudad de México"
          />
        </div>
      )}

      <div>
        <label className={`text-[11px] uppercase tracking-[0.22em] block mb-2 ${labelCls}`}>
          Correo electrónico
        </label>
        <input
          type="email"
          required
          data-testid={`${testidPrefix}-email`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`w-full ${inputBase} py-3 text-base focus:outline-none transition-colors`}
          placeholder="tu@correo.com"
        />
      </div>

      {Honeypot}

      <button
        type="submit"
        disabled={loading}
        data-testid={`${testidPrefix}-submit`}
        className="mt-2 w-full sm:w-auto inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-7 py-3.5 text-sm font-medium hover:bg-[#A64826] transition-all duration-[400ms] disabled:opacity-60"
      >
        {loading ? "Enviando…" : isCommunity ? "Unirme a la comunidad" : "Suscribirme"}
        <ArrowRight className="h-4 w-4" />
      </button>

      <p className={`text-xs pt-1 ${onDark ? "text-cream/50" : "text-charcoal/50"}`}>
        Al enviar aceptas recibir correos de Mujeres Motor. Puedes darte de baja cuando quieras.
      </p>
    </form>
  );
}
